import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type CreateCompanyAccountPayload,
  type CreateCompanyAccountResponse,
} from './types';
import {
  zodCreateCompanyAccountEndpointSchema,
  zodCreateCompanyAccountPayloadSchema,
  zodCreateCompanyAccountResponseSchema,
} from './validations';

/**
 * Creates a new company account.
 *
 * @param companyAccountData - An object containing the company account details to be created.
 * @returns - A promise that resolves with the API response.
 * @throws If the response is missing `companyAccountId` or `baseOuId` — both
 *   are nullable in the OpenAPI schema, but every caller immediately uses
 *   them to navigate to the new account or grant permissions on it, so a
 *   response missing either cannot be treated as a successful create.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company-Account/operation/create_company_acc_company_account_post
 */
export const createCompanyAccount = async (
  companyAccountData: CreateCompanyAccountPayload,
) => {
  try {
    const response = await vestigasApi
      .post(ENDPOINT.COMPANY.ACCOUNT.CREATE(), {
        json: companyAccountData,
        validate: {
          body: zodCreateCompanyAccountPayloadSchema,
          pathSegments: zodCreateCompanyAccountEndpointSchema,
          response: zodCreateCompanyAccountResponseSchema,
        },
      })
      .json<CreateCompanyAccountResponse>();

    if (!response.companyAccountId || !response.baseOuId) {
      throw new Error(
        'Create company account response is missing companyAccountId or baseOuId',
      );
    }

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating company account',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 409: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.createCompanyAccount.error409,
            { ns: 'data' },
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.createCompanyAccount.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.companyAccount.createCompanyAccount.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
